import { evaluateDipSetup } from "./dipChecklist.js";
import { evaluateLongTerm } from "./longTermChecklist.js";
import { evaluateMomentum } from "./momentumChecklist.js";
import { evaluatePutsChecklist } from "./putsChecklist.js";
import {
  applyMomentumHysteresis,
  applyPutsHysteresis,
} from "./checklistHysteresis.js";

function promoteMomentumEntryOk(checklist, result) {
  if (!checklist || checklist.status !== "SETUP") return checklist;
  const gate = result?.gate?.label ?? "WAIT";
  const gateBlocked = result?.gate?.blocked || gate === "BLOCKED";
  const rr1 = result?.rr1;
  if (gate === "GO" && !gateBlocked && rr1 != null && rr1 >= 1.5) {
    return { ...checklist, status: "ENTRY_OK", reason: "" };
  }
  return checklist;
}

function promotePutsEntryOk(checklist, result) {
  if (!checklist || checklist.status !== "SETUP") return checklist;
  const gateBlocked = result?.gate?.blocked || result?.gate?.label === "BLOCKED";
  const rr = checklist.putsRr1 ?? result?.putsRr1;
  if (!gateBlocked && rr != null && rr >= 1.5) {
    return { ...checklist, status: "ENTRY_OK", reason: "" };
  }
  return checklist;
}

/** Attach DIP + LONG_TERM + momentum/puts checklists (independent of legacy score gate). */
export function attachScanLanes(result, ctx = {}) {
  if (!result) return result;
  const marketCtx = ctx.marketCtx ?? result.marketCtx ?? {};
  result.marketCtx = marketCtx;

  result.dipChecklist = evaluateDipSetup(result, marketCtx);
  result.longTerm = evaluateLongTerm(
    result,
    ctx.fundamentals ?? null,
    ctx.weeklyBars ?? null,
    marketCtx,
    { tickerSectorMap: ctx.tickerSectorMap ?? {} }
  );

  let momentum = evaluateMomentum(result);
  let puts = evaluatePutsChecklist(result);

  const prior = ctx.priorScannerState ?? ctx.scannerStatePrior ?? null;
  if (prior?.momentum_status) {
    momentum = applyMomentumHysteresis(momentum, prior.momentum_status, result);
    momentum = promoteMomentumEntryOk(momentum, result);
  }
  if (prior?.puts_status) {
    puts = applyPutsHysteresis(puts, prior.puts_status, result);
    puts = promotePutsEntryOk(puts, result);
  }

  result.momentumChecklist = momentum;
  result.putsChecklist = puts;
  result.momentum_status = momentum.status;
  result.momentum_grade = momentum.grade;
  result.puts_status = puts.status;
  result.puts_grade = puts.grade;

  result.rsDuringDrop = result.dipChecklist.rsDuringDrop ?? null;
  result.dip_status = result.dipChecklist.status;
  result.lt_status = result.longTerm?.status ?? "NONE";
  result.lt_quality = result.longTerm?.qualityScore ?? null;
  result.lt_discount_reasons = result.longTerm?.discountReasons ?? [];
  result.lt_tranches = result.longTerm?.tranches ?? [];

  const actionLabel = result.scannerAction?.label ?? "";
  if (/DIP/i.test(actionLabel)) result.setup_lane = "DIP";
  else if (/PUTS/i.test(actionLabel)) result.setup_lane = "PUTS";
  else if (result.lt_status === "BUY_ZONE" && !/CALLS|DIP|PUTS/i.test(actionLabel)) {
    result.setup_lane = "LONG_TERM";
  } else result.setup_lane = "MOMENTUM";

  return result;
}

export function refreshSetupLane(result) {
  if (!result) return;
  const label = result.scannerAction?.label ?? "";
  if (/DIP/i.test(label)) result.setup_lane = "DIP";
  else if (/PUTS/i.test(label)) result.setup_lane = "PUTS";
  else if (/CALLS|MOMENTUM|CONSIDER|BOTTOM|FORMING/i.test(label)) result.setup_lane = "MOMENTUM";
  else if (result.lt_status === "BUY_ZONE") result.setup_lane = "LONG_TERM";
  else result.setup_lane = result.setup_lane || "MOMENTUM";
}
